import OpenAI from 'openai';

// OpenAI TTS API client for text-to-speech synthesis
// This file provides a client for the OpenAI TTS API to generate high-quality voice audio

interface OpenAITTSOptions {
  voice?: 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer';
  model?: 'tts-1' | 'tts-1-hd';
  speed?: number; // 0.25 to 4.0
}

interface OpenAIVoice {
  voice_id: string;
  name: string;
  category: string;
  description?: string;
  preview_url?: string;
  settings?: {
    speed: number;
  };
}

class OpenAITTSClient {
  private openai: OpenAI;
  private defaultModel: string = 'tts-1';
  private requestsInLastMinute: number = 0;
  private lastRequestTimestamp: number = 0;
  private maxRequestsPerMinute: number = 50; // Conservative limit to prevent overuse
  private requestQueue: Array<() => Promise<any>> = [];
  private isProcessingQueue: boolean = false;
  private isAvailable: boolean = true; // Track if OpenAI TTS is available

  constructor(apiKey: string = '') {
    const key = apiKey || import.meta.env.VITE_OPENAI_API_KEY || 'demo-api-key';
    
    this.openai = new OpenAI({
      apiKey: key,
      dangerouslyAllowBrowser: true
    });
    
    // Reset request counter every minute
    setInterval(() => {
      this.requestsInLastMinute = 0;
    }, 60000);
    
    // Check if API key is valid
    this.checkApiKeyValidity();
  }

  /**
   * Check if the API key is valid
   */
  private async checkApiKeyValidity() {
    if (!this.openai.apiKey || this.openai.apiKey === 'demo-api-key' || this.openai.apiKey === 'your-openai-api-key') {
      console.log('OpenAI API key not provided or using demo key. Voice synthesis will use fallback.');
      this.isAvailable = false;
      return;
    }
    
    try {
      // Test the API key by making a simple request
      await this.openai.models.list();
      console.log('OpenAI API key validated successfully');
      this.isAvailable = true;
    } catch (error) {
      console.warn(`OpenAI API key validation failed:`, error);
      this.isAvailable = false;
    }
  }

  /**
   * Queue a request to be processed when rate limits allow
   */
  private async queueRequest<T>(operation: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      // Add the operation to the queue
      this.requestQueue.push(async () => {
        try {
          const result = await this.retryWithBackoff(operation);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      });
      
      // Start processing the queue if not already processing
      if (!this.isProcessingQueue) {
        this.processQueue();
      }
    });
  }

  /**
   * Process the request queue with rate limiting
   */
  private async processQueue() {
    if (this.isProcessingQueue || this.requestQueue.length === 0) {
      return;
    }
    
    this.isProcessingQueue = true;
    
    try {
      while (this.requestQueue.length > 0) {
        // Check if we're within rate limits
        const now = Date.now();
        const timeSinceLastRequest = now - this.lastRequestTimestamp;
        
        // If we've hit the rate limit, wait before continuing
        if (this.requestsInLastMinute >= this.maxRequestsPerMinute && timeSinceLastRequest < 60000) {
          const waitTime = 60000 - timeSinceLastRequest + 100; // Add 100ms buffer
          console.log(`Rate limit reached, waiting ${waitTime}ms before next request`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          this.requestsInLastMinute = 0;
        }
        
        // Process the next request
        const nextRequest = this.requestQueue.shift();
        if (nextRequest) {
          this.requestsInLastMinute++;
          this.lastRequestTimestamp = Date.now();
          await nextRequest();
        }
        
        // Small delay between requests to prevent bursts
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    } finally {
      this.isProcessingQueue = false;
    }
  }

  /**
   * Retry mechanism with exponential backoff
   */
  private async retryWithBackoff<T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    baseDelay: number = 1000
  ): Promise<T> {
    let lastError: Error;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;
        
        // Don't retry if it's not a rate limit error or if we've exhausted retries
        if (attempt === maxRetries || !this.isRetryableError(error)) {
          throw error;
        }

        // Calculate delay with exponential backoff and jitter
        const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
        console.log(`Rate limit hit, retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError!;
  }

  /**
   * Check if an error is retryable (rate limit or temporary server error)
   */
  private isRetryableError(error: any): boolean {
    if (error?.message?.includes('429')) return true; // Rate limit
    if (error?.message?.includes('502')) return true; // Bad gateway
    if (error?.message?.includes('503')) return true; // Service unavailable
    if (error?.message?.includes('504')) return true; // Gateway timeout
    return false;
  }

  /**
   * Check if we should use the API or fallback
   */
  private shouldUseAPI(): boolean {
    // Don't use API if no key, demo key, or API is not available
    if (!this.openai.apiKey || this.openai.apiKey === 'demo-api-key' || this.openai.apiKey === 'your-openai-api-key' || !this.isAvailable) {
      return false;
    }
    
    return true;
  }

  /**
   * Synthesize speech from text using OpenAI TTS
   */
  async synthesizeSpeech(
    text: string,
    options: OpenAITTSOptions = {}
  ): Promise<ArrayBuffer> {
    try {
      // If we shouldn't use the API, return empty buffer to trigger fallback
      if (!this.shouldUseAPI()) {
        console.log("Using demo mode for OpenAI TTS synthesis");
        return new ArrayBuffer(0);
      }

      // Queue the request to respect rate limits
      return await this.queueRequest(async () => {
        // Check text length to avoid unnecessary API calls
        if (!text || text.trim().length === 0) {
          console.warn("Empty text provided to synthesizeSpeech, returning empty buffer");
          return new ArrayBuffer(0);
        }
        
        // Limit text length to avoid excessive credit usage
        const maxChars = 4096; // OpenAI TTS limit
        if (text.length > maxChars) {
          console.warn(`Text too long (${text.length} chars), truncating to ${maxChars} chars`);
          text = text.substring(0, maxChars) + "...";
        }
        
        const response = await this.openai.audio.speech.create({
          model: options.model || this.defaultModel,
          voice: options.voice || 'nova',
          input: text,
          speed: options.speed || 1.0
        });

        return await response.arrayBuffer();
      });
    } catch (error) {
      console.warn('OpenAI TTS synthesis error:', error);
      
      // If there's a network error or other issue, disable OpenAI TTS temporarily
      if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        console.warn('OpenAI TTS authentication failed. Disabling OpenAI TTS.');
        this.isAvailable = false;
      }
      
      return new ArrayBuffer(0);
    }
  }

  /**
   * Get all available voices
   */
  async getVoices(): Promise<OpenAIVoice[]> {
    try {
      // OpenAI TTS has predefined voices, so we return them directly
      return [
        { 
          voice_id: 'alloy', 
          name: 'Alloy', 
          category: 'general',
          description: 'A balanced, neutral voice suitable for most content'
        },
        { 
          voice_id: 'echo', 
          name: 'Echo', 
          category: 'general',
          description: 'A clear, articulate voice with good pronunciation'
        },
        { 
          voice_id: 'fable', 
          name: 'Fable', 
          category: 'storytelling',
          description: 'A warm, engaging voice perfect for narratives'
        },
        { 
          voice_id: 'onyx', 
          name: 'Onyx', 
          category: 'professional',
          description: 'A deep, authoritative voice for serious content'
        },
        { 
          voice_id: 'nova', 
          name: 'Nova', 
          category: 'therapeutic',
          description: 'A gentle, caring voice ideal for mental wellness'
        },
        { 
          voice_id: 'shimmer', 
          name: 'Shimmer', 
          category: 'uplifting',
          description: 'A bright, energetic voice for positive content'
        }
      ];
    } catch (error) {
      console.warn('OpenAI TTS getVoices error:', error);
      // Return default voices as fallback
      return [
        { voice_id: 'nova', name: 'Nova', category: 'therapeutic' },
        { voice_id: 'alloy', name: 'Alloy', category: 'general' },
        { voice_id: 'echo', name: 'Echo', category: 'general' },
        { voice_id: 'shimmer', name: 'Shimmer', category: 'uplifting' }
      ];
    }
  }

  /**
   * Get details for a specific voice
   */
  async getVoice(voiceId: string): Promise<OpenAIVoice> {
    try {
      const voices = await this.getVoices();
      const voice = voices.find(v => v.voice_id === voiceId);
      
      if (voice) {
        return voice;
      }
      
      return {
        voice_id: voiceId,
        name: 'Unknown Voice',
        category: 'general'
      };
    } catch (error) {
      console.warn('OpenAI TTS getVoice error:', error);
      return {
        voice_id: voiceId,
        name: 'Fallback Voice',
        category: 'general'
      };
    }
  }

  /**
   * Convert text to speech and play it directly
   */
  async speakText(
    text: string,
    voiceId: string,
    options: OpenAITTSOptions = {}
  ): Promise<HTMLAudioElement> {
    try {
      const audioData = await this.synthesizeSpeech(text, {
        ...options,
        voice: voiceId as any
      });
      
      // If we got an empty buffer, use fallback
      if (audioData.byteLength === 0) {
        return this.fallbackSpeak(text);
      }
      
      // Create blob and URL
      const blob = new Blob([audioData], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      
      // Create and play audio element
      const audio = new Audio(url);
      
      // Clean up URL when done
      audio.onended = () => {
        URL.revokeObjectURL(url);
      };
      
      await audio.play();
      return audio;
    } catch (error) {
      console.error('Failed to speak text:', error);
      return this.fallbackSpeak(text);
    }
  }

  /**
   * Fallback to browser speech synthesis
   */
  private async fallbackSpeak(text: string): Promise<HTMLAudioElement> {
    return new Promise((resolve, reject) => {
      if (!('speechSynthesis' in window)) {
        reject(new Error('Speech synthesis not supported'));
        return;
      }

      // Stop any current speech
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      
      // Configure voice
      const voices = window.speechSynthesis.getVoices();
      const selectedVoice = voices.find(voice => 
        voice.name.toLowerCase().includes('female') || 
        voice.name.toLowerCase().includes('samantha') ||
        voice.name.toLowerCase().includes('karen')
      ) || voices[0];

      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }

      utterance.rate = 0.9;
      utterance.pitch = 1.0;
      utterance.volume = 0.8;

      // Create a dummy audio element to return
      const dummyAudio = new Audio();
      
      utterance.onend = () => {
        resolve(dummyAudio);
      };

      utterance.onerror = (event) => {
        reject(new Error(`Speech synthesis error: ${event.error}`));
      };

      window.speechSynthesis.speak(utterance);
    });
  }
}

// Export a singleton instance
export const openaiTTS = new OpenAITTSClient();

// Export types for use in other files
export type { OpenAITTSOptions, OpenAIVoice };